/* eslint-disable max-lines */
import moment from 'moment-timezone'
import _ from 'underscore'

import {
  ACCESSORIES_PLOT_BAND_Y_AXIS_NAME,
  AIRFLOW_STEPS_Y_AXIS_NAME,
  ALERTS_PLOT_BAND_Y_AXIS_NAME,
  ID_PLOT_BAND_Y_AXIS_NAME,
  OD_PLOT_BAND_Y_AXIS_NAME,
  TEMP_HUMIDITY_Y_AXIS_NAME,
  buildLayout,
  zoneColors
} from 'pages/Device/ThermostatRthPanel/Chart/chartOptions'
import { TracesConfig, StageTracesConfig, ZoneTracesConfig } from 'pages/Device/ThermostatRthPanel/Chart/parametersMap'
import SuperheatInterpolator from 'pages/Device/ThermostatRthPanel/Chart/superheatInterpolator'
import { updateAlertsSeverity } from 'helpers/alerts'

const GRAPHABLE_DATA = ['zones', 'outdoorHumidity', 'outdoorTemps', 'stages', 'integratedDehumidification', 'alarmOccurrences']

export const OPERATIONS = {
  'SYSTEM_MODE_OFF': { name: 'System Mode Off', mode: 'off', category: 'off', color: '#6B6B6B' }, // medium grey
  'DEFROST': { name: 'Defrost', mode: 'heating', category: 'outdoor', color: '#FFA500' }, // orange
  'COMPRESSOR_COOLING': { name: 'Compressor Cooling', mode: 'cooling', category: 'outdoor', color: '#1F729D' }, // dark blue
  'COMPRESSOR_COOLING_STAGE_1': { name: 'Compressor Cooling Stage 1', mode: 'cooling', category: 'outdoor', color: '#6BBEEA' }, // light blue
  'COMPRESSOR_COOLING_STAGE_2': { name: 'Compressor Cooling Stage 2', mode: 'cooling', category: 'outdoor', color: '#1F729D' }, // dark blue
  'COMPRESSOR_HEATING': { name: 'Compressor Heating', mode: 'heating', category: 'outdoor', color: '#BC1C10' }, // dark red
  'COMPRESSOR_HEATING_STAGE_1': { name: 'Compressor Heating Stage 1', mode: 'heating', category: 'outdoor', color: '#FC6C68' }, // medium red
  'COMPRESSOR_HEATING_STAGE_2': { name: 'Compressor Heating Stage 2', mode: 'heating', category: 'outdoor', color: '#BC1C10' }, // dark red
  'INDOOR_HEATING': { name: 'Indoor Heating', mode: 'heating', category: 'indoor', color: '#BC1C10' }, // dark red
  'INDOOR_HEATING_STAGE_1': { name: 'Indoor Heating Stage 1', mode: 'heating', category: 'indoor', color: '#FF9DA6' }, // light red
  'INDOOR_HEATING_STAGE_2': { name: 'Indoor Heating Stage 2', mode: 'heating', category: 'indoor', color: '#FC6C68' }, // medium red
  'INDOOR_HEATING_STAGE_3': { name: 'Indoor Heating Stage 3', mode: 'heating', category: 'indoor', color: '#BC1C10' }, // dark red
  'INDOOR_FAN_ONLY': { name: 'Indoor Fan Only', mode: 'fan on', category: 'indoor', color: '#89A982' }, // green
  'ELECTRIC_HEATING_STAGE_1': { name: 'Electric Heating Stage 1', mode: 'heating', category: 'indoor', color: '#FF9DA6' }, // light red
  'ELECTRIC_HEATING_STAGE_2': { name: 'Electric Heating Stage 2', mode: 'heating', category: 'indoor', color: '#FC6C68' }, // medium red
  'ELECTRIC_HEATING_STAGE_3': { name: 'Electric Heating Stage 3', mode: 'heating', category: 'indoor', color: '#BC1C10' }, // dark red
  'GAS_HEATING_STAGE_1': { name: 'Gas Heating Stage 1', mode: 'heating', category: 'indoor', color: '#FF9DA6' }, // light red
  'GAS_HEATING_STAGE_2': { name: 'Gas Heating Stage 2', mode: 'heating', category: 'indoor', color: '#FC6C68' }, // medium red
  'GAS_HEATING_STAGE_3': { name: 'Gas Heating Stage 3', mode: 'heating', category: 'indoor', color: '#BC1C10' }, // dark red
  'HYDRONIC_HEATING_STAGE_1': { name: 'Hydronic Heating Stage 1', mode: 'heating', category: 'indoor', color: '#FC6C68' }, // medium red
  'INTEGRATED_DEHUMIDIFICATION': { name: 'Integrated Dehumidification (IDS)', mode: 'cooling', category: 'ids', color: '#00FF00' }, // light green
  'QUIET_MODE': { name: 'Quiet Mode', mode: 'quiet mode', category: 'quiet mode', color: '#D1EA13' }, // green yellow
  'HUMIDIFIER': { name: 'Humidifier', mode: 'humidifier', category: 'accessories', color: '#F3B879' }, // orange light
  'DEHUMIDIFIER': { name: 'Dehumidifier', mode: 'dehumidifier', category: 'accessories', color: '#F8EE08' }, // yellow
  'VENTILATOR': { name: 'Ventilator', mode: 'ventilator', category: 'accessories', color: '#9A89FF' } // purple
}

export const parseRthData = (rawData, timeZone, fromTime, toTime) => {
  if (!rawData || !timeZone || !fromTime || !toTime) {
    return { traces: null, zoneTraces: null, stageTraces: null, alerts: null, layout: null }
  }

  const rthData = parseData(rawData)

  const stageWithIds = addIdsToStages(rthData.stages, rthData?.integratedDehumidification)
  const stagesWithAccessories = addAccessoriesToStages(stageWithIds, rthData.accessoriesOperationOccurrence)
  const traces = parseOccurrences(rthData, timeZone)
  const zones = parseZones(rthData.zones, timeZone)
  const stages = parseStages(stagesWithAccessories, timeZone)
  const alerts = parseAlerts(rthData.alarmOccurrences)

  if (_.all(traces.concat(zones).concat(stages), (trace) => trace.x.length === 0)) { // No data
    return { traces: null, zoneTraces: null, stageTraces: null, alerts: null, layout: null }
  }

  const updatedBaseLayout = buildLayout(traces.concat(zones).concat(stages), rthData)
  const layoutWithXaxisRange = updateXaxisTimeRange(updatedBaseLayout, fromTime, toTime, timeZone)

  return { traces: traces.concat(emptyTraces), zoneTraces: zones, stageTraces: stages, alerts, layout: layoutWithXaxisRange }
}

export const updateXaxisTimeRange = (layout, fromTime, toTime, timeZone) => {
  if (!layout) return null

  const range = [
    moment.tz(fromTime, timeZone).format('YYYY-MM-DDTHH:mm:ss.SSS'),
    moment.tz(toTime, timeZone).format('YYYY-MM-DDTHH:mm:ss.SSS')
  ]

  return { ...layout, xaxis: { ...layout.xaxis, range } }
}

export const isNoDataAvailable = (rthData) => {
  return !rthData || (rthData && !_.any(GRAPHABLE_DATA, key => _.any(rthData[key])))
}

const parseData = (data) => {
  if (data.integratedDehumidification) {
    data.integratedDehumidification.stage = 'INTEGRATED_DEHUMIDIFICATION'
    data.stages = data.stages.concat(data.integratedDehumidification)
  }
  _.each(data.stages, stage => {
    stage.color = OPERATIONS[stage.stage].color
    stage.mode = OPERATIONS[stage.stage].mode
    stage.name = OPERATIONS[stage.stage].name
  })

  _.each(_.keys(data.accessoriesOperationOccurrence), accessories => {
    if (!_.isEmpty(data.accessoriesOperationOccurrence[accessories])) {
      data.accessoriesOperationOccurrence[accessories] = {
        ...data.accessoriesOperationOccurrence[accessories],
        ...OPERATIONS[accessories.replace('Occurrences', '').toUpperCase()]
      }
    }
  })

  return data
}

const parseOccurrences = (rawData, timeZone) => {
  return _.filter(TracesConfig, (parameter) => _.keys(rawData).includes(parameter.name)).map(r => {
    const parameterName = r.parameterName
    const yaxis = r.yaxis
    const x = []
    const y = []

    if (r.name === 'indoorGasSuperheatOccurrences') {
      parseindoorGasSuperheatOccurrences(rawData[r.name], rawData.stages, rawData.fromTime, rawData.toTime, timeZone, x, y)
    } else {
      rawData[r.name].forEach(s => {
        x.push(parseTimeZone(s.occurredAt, timeZone))
        y.push(s[parameterName])
      })
    }
    const data = createPlotlyTrace(r.seriesName, x, y, yaxis, null, r.mode, r.fill)
    if (r.line) {
      return { ...data, line: r.line }
    } else {
      return data
    }
  })
}

const parseZones = (zones, timeZone) => {
  return (zones || []).map((zone, index) => {
    return ZoneTracesConfig.map(r => {
      const parameterName = r.parameterName
      const yaxis = r.yaxis
      const x = []
      const y = []

      zone[r.name]?.forEach(s => {
        if (r.name === 'relievingOccurrences') {
          processRelievingOccurrences(s, zone, timeZone, x, y)
        } else {
          x.push(parseTimeZone(s.occurredAt, timeZone))
          y.push(s[parameterName])
        }
      })

      const trace = createPlotlyTrace(r.seriesName, x, y, yaxis, zone.name, r.mode)

      return { ...trace, line: { ...(r.line || {}), color: zoneColors[index] }, fill: (r.fill || 'none') } // Add Zone Color
    }).flat()
  }).flat()
}

const parseStages = (stages, timeZone) => {
  const plotlyStageTraces = []

  StageTracesConfig.forEach(traceConfig => {
    let stage = null

    if (Array.isArray(stages)) { // Ruby returns array of stages and go rth service returns object
      stage = stages?.find(r => r.stage === traceConfig.name)
      if (stage && stage.stage === 'QUIET_MODE') {
        stage.runOccurrences = stage.runOccurrences.map((occurrence) => {
          // setting capacity value has plotly required capacity to plot and in go-service this will be already fixed
          return { ...occurrence, capacity: occurrence.operation === 'started' ? 100 : 0 }
        })
      }
    } else {
      stage = stages && stages[traceConfig.name]
    }

    if (stage) {
      const x = []
      const y = []
      const parameterName = traceConfig.parameterName

      stage.runOccurrences.forEach((o, index) => {
        const occurredAt = parseTimeZone(o.occurredAt, timeZone)
        if (index === 0 || _.last(y) === null) { // starts the cycle
          x.push(occurredAt)
          y.push(traceConfig.min) // offset from the bttom of the band. default is 0. 80 for IDS and Quiet Mode.
        }

        x.push(occurredAt)
        // sets max value of a operation capacity can be displayed. default is 100
        y.push(Math.max(traceConfig.min, Math.min(traceConfig.max, o[parameterName])))

        if (!o[parameterName]) { // If Capacity is zero or Operation ends. Create gaps
          x.push(occurredAt)
          y.push(null)
        }
      })

      traceConfig.yaxis.forEach((axis) => { // Create additional trace. Example: System Mode displays on both Indoor and Outdoor Band
        const data = createPlotlyTrace(traceConfig.seriesName, x, y, axis)

        plotlyStageTraces.push({
          ...data,
          hoverinfo: 'skip',
          line: traceConfig.line,
          fillcolor: traceConfig.fillcolor,
          fill: traceConfig.fill,
          connectgaps: false
        })
      })
    }
  })

  return plotlyStageTraces
}

export const parseAlerts = (alarmOccurrences) => {
  const uniqueAlerts = getUniqueAlertOccurrences(alarmOccurrences)

  const alerts = (uniqueAlerts || []).map(r => ({
    ...r,
    id: `${r.id}_${r.status}`,
    occurredAt: typeof r.occurredAt === 'string'
      // montblanc uses strings for the TimeStamp FP type
      ? moment(r.occurredAt).format('YYYY-MM-DDTHH:mm:ss.SSS')
      : parseTimeZone(r.occurredAt, r.timeZone),
    severity: r.severity?.toLowerCase()
  }))

  return updateAlertsSeverity(alerts)
}

const getUniqueAlertOccurrences = (alertOccurrences) => ( // we get same alerts multiple time so filtering that here
  _.uniq(alertOccurrences, (alert) => (
    alert.code + alert.status + alert.serialId + alert.occurredAt)
  )
)

const parseindoorGasSuperheatOccurrences = (superheatOccurrences, stages, fromTime, toTime, timeZone, x, y) => {
  const start = moment.tz(fromTime, timeZone).unix()
  const end = moment.tz(toTime, timeZone).unix()
  const occurrences = SuperheatInterpolator.interpolate(superheatOccurrences, stages, start, end)
  occurrences.forEach(r => {
    x.push(parseTimeZone(r.occurredAt, timeZone))
    y.push(r.temperature)
  })
}

const processRelievingOccurrences = (relievingOccurrence, zone, timeZone, x, y) => {
  const point = interpolateTempPoint(relievingOccurrence.occurredAt, zone.tempOccurrences)
  if (point) {
    x.push(parseTimeZone(point.occurredAt, timeZone))
    y.push(point.temperature)
  }
}

const interpolateTempPoint = (target, set) => {
  const reversedSet = set.slice(0).reverse()
  let leftPoint = _.find(reversedSet, occurrence => target >= occurrence.occurredAt)
  if (!leftPoint) { leftPoint = _.extend(_.first(set), { occurredAt: target }) }
  let rightPoint = _.find(set, occurrence => target <= occurrence.occurredAt)
  if (!rightPoint) { rightPoint = _.extend(_.last(set), { occurredAt: target }) }
  return interpolatePoints([leftPoint, rightPoint], target)
}

const interpolatePoints = (pair, time) => {
  if (!pair[0]) { return pair[1] }

  const tempDelta = pair[1].temperature - pair[0].temperature
  const timeDelta = (time - pair[0].occurredAt)
  const timeSpan = (pair[1].occurredAt - pair[0].occurredAt)

  const scaledTemp =
    tempDelta === 0
      ? pair[0].temperature
      : pair[0].temperature + (tempDelta * (timeDelta / timeSpan))

  return { occurredAt: time, temperature: scaledTemp }
}

const createPlotlyTrace = (seriesName, x, y, yaxis, groupName = null, mode = 'lines', fill = '') => {
  return {
    seriesName,
    group: groupName,
    visible: true,
    type: 'scatter',
    mode: mode || 'lines',
    line: {},
    fill,
    marker: {},
    hoverinfo: 'none',
    x,
    y,
    yaxis,
    xaxis: 'x'
  }
}

const parseTimeZone = (timestamp, timeZone, addMilliseconds = 0) => {
  const t = moment.unix(timestamp).tz(timeZone)

  return t.add(addMilliseconds, 'milliseconds').format('YYYY-MM-DDTHH:mm:ss.SSS')
}

// TODO: This method is not needed if we are using "Go RTH Service"
// integratedDehumidification included in stages object in Go RTH Service
const addIdsToStages = (stages, ids) => {
  if (!ids) { return stages }

  const newRunOccurrences = []
  // updating capacity has plotly need capacity
  ids.runOccurrences.forEach((occurrence) => {
    // setting capacity value has plotly required capacity to plot and in go-service this will be already fixed
    newRunOccurrences.push({ ...occurrence, capacity: occurrence.operation === 'started' ? 100 : 0 })
  })

  ids.runOccurrences = newRunOccurrences

  return [...stages, {
    stage: 'INTEGRATED_DEHUMIDIFICATION',
    ...ids
  }]
}

// TODO: This method is not needed if we are using "Go RTH Service"
// Accessories included in stages object in Go RTH Service
const addAccessoriesToStages = (stages, accessories) => {
  if (!accessories) { return stages }

  const stagesWithAccessories = [...stages]

  if (accessories.ventilatorOccurrences) {
    stagesWithAccessories.push({
      stage: 'VENTILATOR',
      ...accessories.ventilatorOccurrences
    })
  }

  if (accessories.dehumidifierOccurrences) {
    stagesWithAccessories.push({
      stage: 'DEHUMIDIFIER',
      ...accessories.dehumidifierOccurrences
    })
  }

  if (accessories.humidifierOccurrences) {
    stagesWithAccessories.push({
      stage: 'HUMIDIFIER',
      ...accessories.humidifierOccurrences
    })
  }

  return stagesWithAccessories
}

// Need to add empty traces to display band titles even there is no data
const emptyTraces = [
  { x: [], y: [], yaxis: ACCESSORIES_PLOT_BAND_Y_AXIS_NAME, type: 'scatter' },
  { x: [], y: [], yaxis: ID_PLOT_BAND_Y_AXIS_NAME, type: 'scatter' },
  { x: [], y: [], yaxis: OD_PLOT_BAND_Y_AXIS_NAME, type: 'scatter' },
  { x: [], y: [], yaxis: ALERTS_PLOT_BAND_Y_AXIS_NAME, type: 'scatter' },
  { x: [], y: [], yaxis: TEMP_HUMIDITY_Y_AXIS_NAME, type: 'scatter' },
  { x: [], y: [], yaxis: AIRFLOW_STEPS_Y_AXIS_NAME, type: 'scatter' }
]
